import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { GAMES, FAMILY } from "@/lib/games";
import { formatDate, commonNumberDate } from "@/lib/format";
import { CaretLeft } from "@phosphor-icons/react";
import CommonNumberContent from "@/components/CommonNumberContent";

/* ----------------------------------------------------------------------- */
/* Deterministic daily Common Number generator.                            */
/*  - Same for every visitor on the same day (per game).                   */
/*  - Rolls over automatically at 8:00 PM IST to next day's numbers.       */
/*  - Purely computed on the frontend from (slug, date) — no admin needed. */
/* ----------------------------------------------------------------------- */
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}
function seed(str, salt) {
  return fnv1a(`${str}|${salt}`);
}
function pick2(str, salt) {
  return String(seed(str, salt) % 100).padStart(2, "0");
}
function buildCN(slug, date) {
  const frDirect = pick2(slug + "@" + date, "fr-direct");
  const frRev = frDirect.split("").reverse().join("");
  const srDirect = pick2(slug + "@" + date, "sr-direct");
  const srRev = srDirect.split("").reverse().join("");
  return {
    fr: { direct: [frDirect, frRev], house: frDirect[0], ending: frDirect[1] },
    sr: { direct: [srDirect, srRev], house: srDirect[0], ending: srDirect[1] },
  };
}

/** Common-number "for date" — delegates rollover rules to commonNumberDate(game). */
function cnDate(game) {
  return commonNumberDate(game);
}

export default function CommonNumbers({ slug }) {
  const game = GAMES.find((g) => g.slug === slug);
  const [cnCurrentDate, setCnCurrentDate] = useState(() => (game ? cnDate(game) : ""));

  // Auto-detect rollover (8 PM morning/day, 1 AM night) without a manual refresh.
  useEffect(() => {
    if (!game) return;
    const tick = () => setCnCurrentDate(cnDate(game));
    tick();
    const id = setInterval(tick, 60_000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [game]);

  const cn = useMemo(() => (game && cnCurrentDate ? buildCN(game.slug, cnCurrentDate) : null), [game, cnCurrentDate]);
  if (!game || !cn) return null;
  const f = FAMILY[game.color_family];
  const path = `/${slug}`;

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <SEO
        title={`${game.name} Common Number Today | Direct Number, House & Ending | Juwai Teer`}
        description={`${game.name} common number for today — Direct Number, House and Ending. Updated automatically every day.`}
        path={`${path}/common-number`}
      />
      <Header />

      <section className={`${f.head}`}>
        <div className="max-w-3xl mx-auto px-4 py-6">
          <Link to={path} className="inline-flex items-center gap-1 text-xs opacity-90 hover:opacity-100" data-testid="back-to-game">
            <CaretLeft size={14} weight="bold" /> Back to {game.name}
          </Link>
          <h1 className="font-heading font-black tracking-tight text-2xl sm:text-3xl mt-2" data-testid="cn-h1">
            {game.name} — Common Number
          </h1>
        </div>
      </section>

      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-8">
        {/* Single card, today only */}
        <div className="rounded-2xl overflow-hidden bg-white border border-neutral-200 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.25)]" data-testid="cn-card">
          <div className="px-5 py-3 border-b border-neutral-100 bg-neutral-50 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-500">Date</div>
            <div className="font-heading font-black text-base sm:text-lg text-neutral-900" data-testid="cn-date">{formatDate(cnCurrentDate)}</div>
          </div>

          <table className="w-full text-center">
            <thead>
              <tr className="bg-gradient-to-r from-violet-900 to-violet-700 text-white uppercase tracking-widest">
                <th className="px-2 py-3 text-[10px] sm:text-xs font-heading font-bold">Round</th>
                <th className="px-2 py-3 text-[10px] sm:text-xs font-heading font-bold">Direct Number</th>
                <th className="px-2 py-3 text-[10px] sm:text-xs font-heading font-bold">House</th>
                <th className="px-2 py-3 text-[10px] sm:text-xs font-heading font-bold">Ending</th>
              </tr>
            </thead>
            <tbody className="font-numbers">
              {[
                { key: "fr", label: "F/R", data: cn.fr },
                { key: "sr", label: "S/R", data: cn.sr },
              ].map(({ key, label, data }, i) => (
                <tr key={key} className={i === 0 ? "border-b border-neutral-200" : ""}>
                  <td className="px-2 py-5 sm:py-6 text-xs sm:text-sm font-heading font-bold uppercase tracking-widest text-neutral-500">{label}</td>
                  <td className="px-2 py-5 sm:py-6">
                    <div className="flex items-center justify-center gap-3 sm:gap-5 font-black text-3xl sm:text-4xl text-neutral-900">
                      <span>{data.direct[0]}</span>
                      <span className="text-neutral-300 text-2xl font-normal">·</span>
                      <span>{data.direct[1]}</span>
                    </div>
                  </td>
                  <td className="px-2 py-5 sm:py-6 font-black text-3xl sm:text-4xl text-neutral-900">{data.house}</td>
                  <td className="px-2 py-5 sm:py-6 font-black text-3xl sm:text-4xl text-neutral-900">{data.ending}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-[11px] text-neutral-500 text-center leading-relaxed">
          Common Number is refreshed automatically — 8:00 PM IST for morning &amp; day games and 1:00 AM IST for
          night games. Numbers shown here are informational only.
        </p>

        <CommonNumberContent game={game} />
      </main>
      <Footer />
    </div>
  );
}
